import { lazy, Suspense } from "react";
import { createBrowserRouter } from "react-router-dom";

// Layout Components
import { PublicLayout } from "./components/layout/PublicLayout";
import { MainLayout } from "./components/layout/MainLayout";
import { BrandSpinner } from "@/components/brand/BrandLoading";

// Lazy-loaded marketing pages
const HomePage = lazy(() =>
  import("./features/marketing").then((m) => ({ default: m.HomePage })),
);
const FeaturesPage = lazy(() =>
  import("./features/marketing").then((m) => ({ default: m.FeaturesPage })),
);
const PricingPage = lazy(() =>
  import("./features/marketing").then((m) => ({ default: m.PricingPage })),
);
const HowItWorksPage = lazy(() =>
  import("./features/marketing").then((m) => ({ default: m.HowItWorksPage })),
);
const SecurityPage = lazy(() =>
  import("./features/marketing").then((m) => ({ default: m.SecurityPage })),
);
const AboutPage = lazy(() =>
  import("./features/marketing").then((m) => ({ default: m.AboutPage })),
);
const BlogPage = lazy(() =>
  import("./features/marketing").then((m) => ({ default: m.BlogPage })),
);
const DevelopersPage = lazy(() =>
  import("./features/marketing").then((m) => ({ default: m.DevelopersPage })),
);
const ResearchPage = lazy(() =>
  import("./features/marketing").then((m) => ({ default: m.ResearchPage })),
);
const PrivacyPage = lazy(() =>
  import("./features/marketing").then((m) => ({ default: m.PrivacyPage })),
);
const TermsPage = lazy(() =>
  import("./features/marketing").then((m) => ({ default: m.TermsPage })),
);

// Lazy-loaded app features
const ChatPage = lazy(() =>
  import("./features/chat").then((m) => ({ default: m.ChatPage })),
);
const ExperiencePage = lazy(() => import("./features/experience").then(module => ({ default: module.ExperiencePage })));
const DashboardPage = lazy(() =>
  import("./features/dashboard").then((m) => ({ default: m.DashboardPage })),
);
const StrategyListPage = lazy(() =>
  import("./features/strategy").then((m) => ({ default: m.StrategyListPage })),
);
const StrategyBuilderPage = lazy(() =>
  import("./features/strategy").then((m) => ({
    default: m.StrategyBuilderPage,
  })),
);
const StrategyDetailPage = lazy(() =>
  import("./features/strategy").then((m) => ({
    default: m.StrategyDetailPage,
  })),
);
const AgentSettingsPage = lazy(() =>
  import("./features/agent-settings").then((m) => ({
    default: m.AgentSettingsPage,
  })),
);
const AgentWalletPage = lazy(() =>
  import("./features/agent-wallet").then((m) => ({
    default: m.AgentWalletPage,
  })),
);
const BindPage = lazy(() =>
  import("./features/bind").then((m) => ({ default: m.BindPage })),
);
const CliAuthPage = lazy(() =>
  import("./features/cli-auth").then((m) => ({ default: m.CliAuthPage })),
);

function LazyWrap({ children }: { children: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center min-h-[60vh]">
          <BrandSpinner size="lg" />
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

export const router = createBrowserRouter([
  // ── Public routes (marketing site) ──────────────────────────
  {
    element: <PublicLayout />,
    children: [
      {
        path: "/",
        element: (
          <LazyWrap>
            <HomePage />
          </LazyWrap>
        ),
      },
      {
        path: "/features",
        element: (
          <LazyWrap>
            <FeaturesPage />
          </LazyWrap>
        ),
      },
      {
        path: "/pricing",
        element: (
          <LazyWrap>
            <PricingPage />
          </LazyWrap>
        ),
      },
      {
        path: "/how-it-works",
        element: (
          <LazyWrap>
            <HowItWorksPage />
          </LazyWrap>
        ),
      },
      {
        path: "/security",
        element: (
          <LazyWrap>
            <SecurityPage />
          </LazyWrap>
        ),
      },
      {
        path: "/about",
        element: (
          <LazyWrap>
            <AboutPage />
          </LazyWrap>
        ),
      },
      {
        path: "/blog",
        element: (
          <LazyWrap>
            <BlogPage />
          </LazyWrap>
        ),
      },
      {
        path: "/developers",
        element: (
          <LazyWrap>
            <DevelopersPage />
          </LazyWrap>
        ),
      },
      {
        path: "/research",
        element: (
          <LazyWrap>
            <ResearchPage />
          </LazyWrap>
        ),
      },
      {
        path: "/legal/privacy",
        element: (
          <LazyWrap>
            <PrivacyPage />
          </LazyWrap>
        ),
      },
      {
        path: "/legal/terms",
        element: (
          <LazyWrap>
            <TermsPage />
          </LazyWrap>
        ),
      },
    ],
  },

  // ── App routes (authenticated agent app) ────────────────────
  {
    element: <MainLayout />,
    children: [
      {
        path: "/chat",
        element: (
          <LazyWrap>
            <ExperiencePage />
          </LazyWrap>
        ),
      },
      {
        path: "/cli-auth",
        element: (
          <LazyWrap>
            <CliAuthPage />
          </LazyWrap>
        ),
      },
      {
        path: "/chat/:sessionId",
        element: (
          <LazyWrap>
            <ExperiencePage />
          </LazyWrap>
        ),
      },
      ...["/trade", "/assets", "/pay", "/send", "/scan", "/receive", "/confirm", "/receipt/:receiptId", "/new-chat", "/fleet/:roomId/rules", "/fleet/:roomId/members"].map(path => ({ path, element: <LazyWrap><ExperiencePage /></LazyWrap> })),
      { path: "/activity/sessions", element: <LazyWrap><ChatPage /></LazyWrap> },
      { path: "/activity/sessions/:sessionId", element: <LazyWrap><ChatPage /></LazyWrap> },
      {
        path: "/dashboard",
        element: (
          <LazyWrap>
            <DashboardPage />
          </LazyWrap>
        ),
      },
      {
        path: "/strategies",
        element: (
          <LazyWrap>
            <StrategyListPage />
          </LazyWrap>
        ),
      },
      {
        path: "/strategies/new",
        element: (
          <LazyWrap>
            <StrategyBuilderPage />
          </LazyWrap>
        ),
      },
      {
        path: "/strategies/:id",
        element: (
          <LazyWrap>
            <StrategyDetailPage />
          </LazyWrap>
        ),
      },
      {
        path: "/strategies/:id/edit",
        element: (
          <LazyWrap>
            <StrategyBuilderPage />
          </LazyWrap>
        ),
      },
      {
        path: "/settings/agent",
        element: (
          <LazyWrap>
            <AgentSettingsPage />
          </LazyWrap>
        ),
      },
      {
        path: "/settings/agent-wallet",
        element: (
          <LazyWrap>
            <AgentWalletPage />
          </LazyWrap>
        ),
      },
      {
        path: "/bind",
        element: (
          <LazyWrap>
            <BindPage />
          </LazyWrap>
        ),
      },
    ],
  },

  // ── 404 ─────────────────────────────────────────────────────
  {
    path: "*",
    element: (
      <div className="flex flex-col items-center justify-center min-h-screen bg-background">
        <h1 className="text-4xl font-bold mb-4 text-foreground">404</h1>
        <p className="text-muted-foreground mb-8">Page not found</p>
        <a href="/" className="text-primary hover:underline">
          Return Home
        </a>
      </div>
    ),
  },
]);
